import {
  descendantLeafDeviceIds,
  DeviceItem,
  DeviceSelectorData,
  groupsFullySelected,
  labsFullySelected,
  LocationNode,
  NodeId,
} from '@/components/scope/DeviceSelector';
import {
  AssignmentDTO,
  CreateAssignmentRequest,
  DeviceDTO,
  GroupDTO,
  LabDTO,
  RoleDTO,
  ScopeType,
  UserDTO,
} from '@/models';
import { deviceService } from '@/services/api/deviceService';
import { groupService } from '@/services/api/groupService';
import { labService } from '@/services/api/labService';
import { roleService } from '@/services/api/roleService';
import { userService } from '@/services/api/userService';
import { useAssignmentStore, useUIStore } from '@/store';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const gKey = (id: string) => `g:${id}`;
const lKey = (id: string) => `l:${id}`;

const buildSelectorData = (
  groups: GroupDTO[],
  labs: LabDTO[],
  devices: DeviceDTO[]
): DeviceSelectorData => {
  const uniqueGroups = Array.from(new Map(groups.map((group) => [group.id, group])).values());
  const uniqueLabs = Array.from(new Map(labs.map((lab) => [lab.id, lab])).values());
  const uniqueDevices = Array.from(new Map(devices.map((device) => [device.id, device])).values());

  const groupIdSet = new Set(uniqueGroups.map((group) => group.id));
  const labIdSet = new Set(uniqueLabs.map((lab) => lab.id));

  const labsByGroup = new Map<string, LabDTO[]>();
  uniqueLabs.forEach((lab) => {
    if (!lab.groupId || !groupIdSet.has(lab.groupId)) return;
    const groupLabs = labsByGroup.get(lab.groupId) ?? [];
    groupLabs.push(lab);
    labsByGroup.set(lab.groupId, groupLabs);
  });

  const devicesByLab = new Map<string, DeviceDTO[]>();
  uniqueDevices.forEach((device) => {
    const labId = device.labId;
    if (!labId || !labIdSet.has(labId)) return;
    const labDevices = devicesByLab.get(labId) ?? [];
    labDevices.push(device);
    devicesByLab.set(labId, labDevices);
  });

  const nodes: LocationNode[] = [];
  const rootIds: string[] = [];

  uniqueGroups.forEach((group) => {
    const childLabs = labsByGroup.get(group.id) ?? [];
    const groupNodeId = gKey(group.id);
    nodes.push({
      id: groupNodeId,
      entityId: group.id,
      name: group.name || 'Unnamed group',
      parentId: null,
      type: 'group',
      childIds: childLabs.map((lab) => lKey(lab.id)),
    });
    rootIds.push(groupNodeId);

    childLabs.forEach((lab) => {
      const labDevices = devicesByLab.get(lab.id) ?? [];
      nodes.push({
        id: lKey(lab.id),
        entityId: lab.id,
        name: lab.name || 'Unnamed lab',
        parentId: groupNodeId,
        type: 'lab',
        childIds: labDevices.map((device) => device.id),
      });
    });
  });

  const deviceItems = uniqueDevices
    .map<DeviceItem | null>((device) => {
      const labId = device.labId;
      if (!labId || !labIdSet.has(labId)) return null;
      return {
        id: device.id,
        name: device.identifier || device.serialNumber || device.id,
        parentId: lKey(labId),
        meta: { serialNumber: device.serialNumber, model: device.model },
      };
    })
    .filter((device): device is DeviceItem => device !== null);

  return { nodes, devices: deviceItems, rootIds };
};

const setsEqual = (left: Set<string>, right: Set<string>) =>
  left.size === right.size && [...left].every((value) => right.has(value));

const selectedDevicesForRole = (
  assignments: AssignmentDTO[],
  roleId: string,
  selectorData: DeviceSelectorData
): Set<NodeId> => {
  const selectedDeviceIds = new Set<NodeId>();
  const deviceIds = new Set(selectorData.devices.map((device) => device.id));

  assignments
    .filter((assignment) => assignment.isActive && assignment.roleId === roleId)
    .forEach((assignment) => {
      if (assignment.scopeType === ScopeType.Device) {
        if (deviceIds.has(assignment.scopeId)) selectedDeviceIds.add(assignment.scopeId);
        return;
      }

      const nodeType = assignment.scopeType === ScopeType.Group ? 'group' : 'lab';
      const node = selectorData.nodes.find(
        (candidate) => candidate.type === nodeType && candidate.entityId === assignment.scopeId
      );
      if (!node) return;

      descendantLeafDeviceIds(node.id, selectorData.nodes, selectorData.devices).forEach(
        (deviceId) => selectedDeviceIds.add(deviceId)
      );
    });

  return selectedDeviceIds;
};

const createDesiredAssignments = (
  roleId: string,
  selectedDeviceIds: Set<NodeId>,
  selectorData: DeviceSelectorData
): CreateAssignmentRequest[] => {
  const labIds = labsFullySelected(selectedDeviceIds, selectorData.nodes, selectorData.devices);
  const fullySelectedLabIds = new Set(labIds);
  const groupIds = groupsFullySelected(fullySelectedLabIds, selectorData.nodes);
  const fullySelectedGroupIds = new Set(groupIds);

  const nodeById = new Map(selectorData.nodes.map((node) => [node.id, node]));
  const labIdsCoveredByGroups = new Set<string>();
  selectorData.nodes
    .filter((node) => node.type === 'group' && fullySelectedGroupIds.has(node.entityId))
    .forEach((groupNode) => {
      groupNode.childIds.forEach((labNodeId) => {
        const labNode = nodeById.get(labNodeId);
        if (labNode) labIdsCoveredByGroups.add(labNode.entityId);
      });
    });

  const assignments: CreateAssignmentRequest[] = groupIds.map((scopeId) => ({
    roleId,
    scopeType: ScopeType.Group,
    scopeId,
  }));

  labIds
    .filter((scopeId) => !labIdsCoveredByGroups.has(scopeId))
    .forEach((scopeId) => {
      assignments.push({ roleId, scopeType: ScopeType.Lab, scopeId });
    });

  selectorData.devices
    .filter((device) => {
      if (!selectedDeviceIds.has(device.id)) return false;
      const labNode = nodeById.get(device.parentId);
      return !labNode || !fullySelectedLabIds.has(labNode.entityId);
    })
    .forEach((device) => {
      assignments.push({ roleId, scopeType: ScopeType.Device, scopeId: device.id });
    });

  return assignments;
};

const isRepresentedInSelector = (
  assignment: AssignmentDTO,
  selectorData: DeviceSelectorData
): boolean => {
  if (assignment.scopeType === ScopeType.Device) {
    return selectorData.devices.some((device) => device.id === assignment.scopeId);
  }

  const nodeType = assignment.scopeType === ScopeType.Group ? 'group' : 'lab';
  const node = selectorData.nodes.find(
    (candidate) => candidate.type === nodeType && candidate.entityId === assignment.scopeId
  );
  return (
    node != null &&
    descendantLeafDeviceIds(node.id, selectorData.nodes, selectorData.devices).length > 0
  );
};

export const useUserScopeManagement = () => {
  const { institutionId, userId } = useParams<{
    institutionId: string;
    userId: string;
  }>();
  const {
    assignments,
    isLoading: isAssignmentLoading,
    error: assignmentError,
    fetchAssignments,
    syncRoleAssignments,
    clearError: clearAssignmentError,
  } = useAssignmentStore();
  const { addNotification } = useUIStore();

  const [user, setUser] = useState<UserDTO | null>(null);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [allGroups, setAllGroups] = useState<GroupDTO[]>([]);
  const [allLabs, setAllLabs] = useState<LabDTO[]>([]);
  const [allDevices, setAllDevices] = useState<DeviceDTO[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(true);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<NodeId>>(new Set());
  const [initialDeviceIds, setInitialDeviceIds] = useState<Set<NodeId>>(new Set());
  const [hasInitialSelection, setHasInitialSelection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!institutionId || !userId) return;
    let cancelled = false;

    const load = async () => {
      setIsLoadingEntities(true);
      setEntityError(null);
      try {
        const [userResult, rolesResult] = await Promise.all([
          userService.getById(institutionId, userId),
          roleService.getAll(),
        ]);
        if (cancelled) return;

        setUser(userResult);
        setRoles(rolesResult);

        const [groupResult, labResult, deviceResult] = await Promise.all([
          groupService.getAll({ page: 1, pageSize: 500 }),
          labService.getAll({ page: 1, pageSize: 500 }),
          deviceService.getAll({
            page: 1,
            pageSize: 500,
            institutionId: userResult.institutionId,
          }),
        ]);
        if (cancelled) return;

        setAllGroups(
          groupResult.data.filter((group) => group.institutionId === userResult.institutionId)
        );
        setAllLabs(labResult.data.filter((lab) => lab.institutionId === userResult.institutionId));
        setAllDevices(
          deviceResult.data.filter((device) => device.institutionId === userResult.institutionId)
        );
      } catch (error: unknown) {
        if (!cancelled) {
          setEntityError(getErrorMessage(error, 'Failed to load assignment data'));
        }
      } finally {
        if (!cancelled) setIsLoadingEntities(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [institutionId, userId]);

  useEffect(() => {
    if (!userId || !user?.institutionId) return;
    fetchAssignments(user.institutionId, userId).catch(() => undefined);
  }, [userId, user?.institutionId, fetchAssignments]);

  const selectorData = useMemo(
    () => buildSelectorData(allGroups, allLabs, allDevices),
    [allGroups, allLabs, allDevices]
  );

  useEffect(() => {
    if (selectedRoleId || roles.length === 0) return;
    const assignedRoleId = assignments.find(
      (assignment) => assignment.isActive && roles.some((role) => role.id === assignment.roleId)
    )?.roleId;
    setSelectedRoleId(assignedRoleId ?? roles[0].id);
  }, [assignments, roles, selectedRoleId]);

  useEffect(() => {
    if (!selectedRoleId || isAssignmentLoading || isLoadingEntities) return;
    const seed = selectedDevicesForRole(assignments, selectedRoleId, selectorData);
    setSelectedDeviceIds(seed);
    setInitialDeviceIds(seed);
    setHasInitialSelection(true);
  }, [assignments, selectedRoleId, selectorData, isAssignmentLoading, isLoadingEntities]);

  const initialSelectedIds = useMemo(() => Array.from(initialDeviceIds), [initialDeviceIds]);

  const handleSelectionChange = useCallback((ids: NodeId[]) => {
    setSelectedDeviceIds(new Set(ids));
  }, []);

  const isDirty = useMemo(
    () => hasInitialSelection && !setsEqual(selectedDeviceIds, initialDeviceIds),
    [hasInitialSelection, selectedDeviceIds, initialDeviceIds]
  );

  const handleRoleChange = useCallback((roleId: string) => {
    setHasInitialSelection(false);
    setSelectedRoleId(roleId);
  }, []);

  const handleSave = useCallback(async () => {
    if (!userId || !user?.institutionId || !selectedRoleId) return;
    setIsSaving(true);
    try {
      const desiredAssignments = createDesiredAssignments(
        selectedRoleId,
        selectedDeviceIds,
        selectorData
      );
      assignments
        .filter(
          (assignment) =>
            assignment.isActive &&
            assignment.roleId === selectedRoleId &&
            !isRepresentedInSelector(assignment, selectorData)
        )
        .forEach((assignment) => {
          desiredAssignments.push({
            roleId: assignment.roleId,
            scopeType: assignment.scopeType,
            scopeId: assignment.scopeId,
          });
        });
      await syncRoleAssignments(user.institutionId, userId, selectedRoleId, desiredAssignments);
      addNotification({
        type: 'success',
        message: 'User assignments updated successfully',
        duration: 2000,
      });
    } catch (error: unknown) {
      addNotification({
        type: 'error',
        message: getErrorMessage(error, 'Failed to update user assignments'),
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    userId,
    user?.institutionId,
    selectedRoleId,
    selectedDeviceIds,
    selectorData,
    assignments,
    syncRoleAssignments,
    addNotification,
  ]);

  const clearError = useCallback(() => {
    clearAssignmentError();
    setEntityError(null);
  }, [clearAssignmentError]);

  return {
    user,
    roles,
    selectedRoleId,
    isLoading: isAssignmentLoading || isLoadingEntities,
    isSaving,
    error: assignmentError ?? entityError,
    isDirty,
    selectorData,
    initialSelectedIds,
    handleRoleChange,
    handleSelectionChange,
    handleSave,
    clearError,
  };
};
